import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGet } from '../../utils/api-client';
import styles from './SwitchRestaurantModal.module.css';

const STORAGE_KEY = 'revyo_current_restaurant_id';

/**
 * SwitchRestaurantModal — modale pour changer de restaurant actif.
 *
 * Comportement :
 *  1. Au mount : fetch GET /api/restaurants/ → liste des restos du user
 *  2. Click sur un resto → localStorage.setItem('revyo_current_restaurant_id', id) + window.location.reload()
 *  3. Bouton "Ajouter un restaurant" → redirect vers /configuration (Phase 3 : modale création)
 */
export default function SwitchRestaurantModal({ onClose }) {
  const { t } = useTranslation('dashboard');
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [currentId, setCurrentId]     = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await apiGet('/api/restaurants/');
        const list = data?.restaurants || data || [];
        setRestaurants(Array.isArray(list) ? list : []);

        // Si pas de current, on prend le premier
        if (!currentId && list[0]?.id) {
          setCurrentId(list[0].id);
        }
      } catch (e) {
        setError(e.message || t('switch_restaurant.error_default'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fermer avec Escape
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const handleSelect = (resto) => {
    if (resto.id === currentId) {
      onClose?.();
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, resto.id);
    } catch (e) {
      console.warn('localStorage indispo:', e);
    }
    // Reload pour que tout le dashboard refetch avec le nouveau resto
    window.location.reload();
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2>🍽️ {t('switch_restaurant.title')}</h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label={t('switch_restaurant.close_label')}>×</button>
        </header>

        {loading && (
          <div className={styles.loading}>🌿 {t('switch_restaurant.loading')}</div>
        )}

        {error && (
          <div className={styles.error}>⚠️ {error}</div>
        )}

        {!loading && !error && restaurants.length === 0 && (
          <div className={styles.empty}>
            <div className={styles.emptyEmoji}>🍽️</div>
            <p>{t('switch_restaurant.empty_title')}</p>
          </div>
        )}

        {!loading && !error && restaurants.length > 0 && (
          <ul className={styles.list}>
            {restaurants.map((r) => {
              const isActive = r.id === currentId;
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    className={`${styles.item} ${isActive ? styles.active : ''}`}
                    onClick={() => handleSelect(r)}
                  >
                    <div className={styles.avatar}>🍽️</div>
                    <div className={styles.body}>
                      <div className={styles.name}>{r.nom}</div>
                      <div className={styles.meta}>
                        {r.ville || t('switch_restaurant.ville_empty')}
                        {r.gmb_connected && <span className={styles.metaTag}>GMB</span>}
                      </div>
                    </div>
                    {isActive && <span className={styles.checkmark}>✓</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.btnAdd}
            onClick={() => {
              onClose?.();
              window.location.href = '/configuration';
            }}
          >
            {t('switch_restaurant.add_button')}
          </button>
        </footer>
      </div>
    </div>
  );
}
